import { Check, FileText } from 'lucide-react';

import { useDocuments } from '../conversations/hooks/use-documents';

import type { DocumentSummary } from '../../lib/types';

/**
 * ColetaDocuments — D6. Lista os Documents anexados à conversa (reuso do fluxo
 * do Brief 2A via `useDocuments(bondId)`) e deixa o atendente marcar quais
 * referenciar em `documentIds`. NÃO duplica upload (o upload vive no Composer da
 * conversa). Sem bond / sem anexos → empty state honesto.
 */
export function ColetaDocuments({
  bondId,
  selectedIds,
  onToggle,
  disabled = false,
}: {
  bondId: string | undefined;
  selectedIds: string[];
  onToggle: (id: string) => void;
  disabled?: boolean;
}): JSX.Element {
  const { documents, isLoading, error } = useDocuments(bondId);

  return (
    <div className="coleta-section">
      <span className="coleta-section__label">Documentos enviados pelo cliente</span>

      {isLoading && <div className="coleta-empty">Carregando documentos…</div>}
      {error !== null && (
        <div className="coleta-empty">Não foi possível carregar os documentos.</div>
      )}

      {!isLoading && error === null && documents.length === 0 && (
        <div className="coleta-empty">
          {bondId === undefined
            ? 'Conversa sem vínculo — nenhum documento anexado para referenciar.'
            : 'Nenhum documento anexado a esta conversa ainda. Anexos enviados no atendimento aparecem aqui.'}
        </div>
      )}

      {documents.map((doc) => (
        <DocRow
          key={doc.id}
          doc={doc}
          selected={selectedIds.includes(doc.id)}
          disabled={disabled}
          onClick={() => onToggle(doc.id)}
        />
      ))}
    </div>
  );
}

function DocRow({
  doc,
  selected,
  disabled,
  onClick,
}: {
  doc: DocumentSummary;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`coleta-doc-row${selected ? ' coleta-doc-row--selected' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
    >
      {selected ? <Check size={14} /> : <FileText size={14} />}
      <span>{doc.name}</span>
      <span className="coleta-doc-row__size">{formatBytes(doc.sizeBytes)}</span>
    </button>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
